import mapValues from 'lodash/mapValues';
import get from 'lodash/get';

import moulages from '~lib/common/config/moulages';
import featureAvailabilityService from '~lib/common/providers/featureAvailabilityService';


import StateEffect from './controls/StateEffect';
import BehaviourEffect from './controls/BehaviourEffect';
import LungSoundEffect from './controls/LungSoundEffect';
import HeartSoundEffect from './controls/HeartSoundEffect';
import PacingEffect from './controls/PacingEffect';
import PhysicalAssessmentEffect from './controls/PhysicalAssessmentEffect';
import MoulageEffect from './controls/MoulageEffect';
import ChangeAnswerEffect from './controls/ChangeAnswerEffect';
import ChangeCallAnswerEffect from './controls/ChangeCallAnswerEffect';
import PlayUnsolicitedEffect from './controls/PlayUnsolicitedEffect';
import ToggleDialogueEffect from './controls/ToggleDialogueEffect';
import ToggleCallEffect from './controls/ToggleCallEffect';
import ToggleLabEffect from './controls/ToggleLabEffect';
import CustomStateEffect from './controls/CustomStateEffect';
import EndConditionEffect from './controls/EndConditionEffect';
import GradingEffect from './controls/GradingEffect';
import PulseStrengthEffect from './controls/PulseStrengthEffect';
import ComplexionEffect from './controls/ComplexionEffect';
import ToggleCustomMenuEffect from './controls/ToggleCustomMenuEffect';
import UnsolicitedCalleeEffect from './controls/UnsolicitedCalleeEffect';
import AiPlayUnsolicitedEffect from './controls/AiPlayUnsolicitedEffect';
import AiChangePromptEffect from './controls/AiChangePromptEffect';
import makeVueControl from './utils/makeVueControl';
import getEnabledRelations from './utils/getEnabledRelations';
import * as ControlUtils from './utils/controlUtils';

const EFFECTS = {
  ENABLE_QUESTION: {
    title: 'Question',
    label: 'Enable question',
    component: ToggleDialogueEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'ENABLE',
        effectType: 'QUESTION',
        placeholder: 'Question',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.questions
      },
    },
    type: 'QUESTION',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areQuestionsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Enable',
  },
  DISABLE_QUESTION: {
    title: 'Question',
    label: 'Disable question',
    component: ToggleDialogueEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'DISABLE',
        effectType: 'QUESTION',
        placeholder: 'Question',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.questions
      },
    },
    type: 'QUESTION',
    isApplicable: effect => !effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areQuestionsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Disable',
  },
  ENABLE_COMMAND: {
    title: 'Command',
    label: 'Enable command',
    component: ToggleDialogueEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'ENABLE',
        effectType: 'COMMAND',
        placeholder: 'Command',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.commands
      },
    },
    type: 'COMMAND',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areCommandsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Enable',
  },
  DISABLE_COMMAND: {
    title: 'Command',
    label: 'Disable command',
    component: ToggleDialogueEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'DISABLE',
        effectType: 'COMMAND',
        placeholder: 'Command',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.commands
      },
    },
    type: 'COMMAND',
    isApplicable: effect => !effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areCommandsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Disable',
  },
  ENABLE_CALL: {
    title: 'Call',
    label: 'Enable call dialogue',
    component: ToggleCallEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'ENABLE',
      },
    },
    type: 'CALL',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areCallsRecommended,
    category: 'Dialogue',
    superCategory: 'Enable',
  },
  DISABLE_CALL: {
    title: 'Call',
    label: 'Disable call dialogue',
    component: ToggleCallEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'DISABLE',
      },
    },
    type: 'CALL',
    isApplicable: effect => !effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areCallsRecommended,
    category: 'Dialogue',
    superCategory: 'Disable',
  },
  ANSWER: {
    title: 'Answer',
    label: 'Change answer',
    component: ChangeAnswerEffect,
    options: {
      props: {
        effectType: 'ANSWER',
        placeholder: 'Question',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.questions,
      }
    },
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areQuestionsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Change',
  },
  AI_ANSWER: {
    title: 'AI Response',
    label: 'Change AI response',
    component: ChangeAnswerEffect,
    options: {
      props: {
        effectType: 'AI_ANSWER',
        placeholder: 'Question/Statement',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.aiQuestions
      }
    },
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areAiQuestionsRecommended,
    isDisabled: ControlUtils.isAiDisabled,
    category: 'Dialogue',
    superCategory: 'Change',
  },
  CALL_ANSWER: {
    title: 'Call Answer',
    label: 'Change call dialogue response',
    component: ChangeCallAnswerEffect,
    options: {
    },
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areCallsRecommended,
    category: 'Dialogue',
    superCategory: 'Change',
  },
  UNSOLICITED_DIALOGUE: {
    title: 'Unsolicited',
    label: 'Play unsolicited patient',
    component: PlayUnsolicitedEffect,
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areUnsolicitedRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Play',
  },
  UNSOLICITED_CALL: {
    title: 'Unsolicited',
    label: 'Play unsolicited callee',
    component: UnsolicitedCalleeEffect,
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areCallsRecommended,
    category: 'Dialogue',
    superCategory: 'Play',
  },
  AI_UNSOLICITED_DIALOGUE: {
    title: 'Unsolicited',
    label: 'Play AI patient dialogue',
    component: AiPlayUnsolicitedEffect,
    noExpiry: true,
    caseSpecific: true,
    isRecommended: ControlUtils.areAiUnsolicitedRecommended,
    isDisabled: ControlUtils.isAiDisabled,
    category: 'Dialogue',
    superCategory: 'Play',
  },
  SIMPLE_VITAL: {
    title: 'Vital',
    label: 'Change vital',
    component: StateEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'vital',
        type: 'VITAL',
        placeholder: 'Select vital',
        hasAdvancedOptions: true,
      },
    },
    type: 'VITAL',
    isApplicable: effect => !effect.matchInput,
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  WAVEFORM: {
    title: 'Waveform',
    label: 'Change waveform',
    component: StateEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'waveform',
        type: 'WAVEFORM',
        placeholder: 'Select waveform',
        useDefault: true,
        hasAdvancedOptions: false,
        noOverTime: true,
      },
    },
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  GLUCOSE: {
    title: 'Glucose',
    label: 'Change glucose',
    component: StateEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'labResult',
        type: 'LAB_MODIFY',
        hasAdvancedOptions: true,
      },
    },
    type: 'LAB_MODIFY',
    isApplicable: effect => effect.id === 'GLUCOSE',
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  LUNG_SOUND: {
    title: 'Lung Sound',
    label: 'Change lung sounds',
    component: LungSoundEffect,
    options: {
      query: require('~lib/common/queries/Sound').fetch,
      queryProp: 'sounds',
    },
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  HEART_SOUND: {
    title: 'Heart Sound',
    label: 'Change heart sound',
    component: HeartSoundEffect,
    options: {
      query: require('~lib/common/queries/Sound').fetch,
      queryProp: 'sounds',
    },
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  ANIMATION: {
    title: 'Behavior',
    label: 'Change behavior',
    component: BehaviourEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    types: ['ANIMATION_ONE_SHOT', 'ANIMATION_REPEAT'],
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  PHYSICAL_ASSESSMENT: {
    title: 'Physical Assessment',
    label: 'Change physical assessment',
    component: PhysicalAssessmentEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    noExpiry: true,
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  EDEMA: {
    types: ['MOULAGE', 'STRUCTURAL_MOULAGE'],
    title: 'Edema',
    label: 'Change edema',
    component: MoulageEffect,
    options: {
      props: {
        placeholder: 'variation',
        moulageCategory: 'EDEMA',
        useStructuralLocations: true,
      },
    },
    noExpiry: true,
    isApplicable: effect => moulages.find(moulage => moulage.category === 'EDEMA' && moulage.key === effect.value),
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  EYE_STATE: {
    title: 'Eye Dilation',
    label: 'Change eye dilation',
    component: StateEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
      props: {
        category: 'eyeState',
        type: 'EYE_STATE',
        placeholder: 'Select eye',
        useDefault: true,
        hasAdvancedOptions: false,
        noOverTime: true,
      },
    },
    noExpiry: true,
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  PALPABLE_PULSE: {
    title: 'Pulse Strength',
    label: 'Change pulse strength',
    component: PulseStrengthEffect,
    options: {},
    noExpiry: true,
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  ENABLE_LAB: {
    title: featureAvailabilityService.translateLabel('Media'),
    label: `Enable ${featureAvailabilityService.translateLabel('Media').toLowerCase()}`,
    component: ToggleLabEffect,
    noExpiry: true,
    options: {
      query: require('~lib/common/queries/LabAsset').fetch,
      queryProp: 'labAssets',
      props: {
        type: 'ENABLE',
      },
    },
    type: 'LAB_ASSET_AVAILABLE',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf._labAssets && caseConf._labAssets.find(t => get(t, 'options.assets', []).length),
    category: featureAvailabilityService.translateLabel('Media'),
    superCategory: 'Enable',
  },
  DISABLE_LAB: {
    title: featureAvailabilityService.translateLabel('Media'),
    label: `Disable ${featureAvailabilityService.translateLabel('Media').toLowerCase()}`,
    component: ToggleLabEffect,
    noExpiry: true,
    options: {
      query: require('~lib/common/queries/LabAsset').fetch,
      queryProp: 'labAssets',
      props: {
        type: 'DISABLE',
      },
    },
    type: 'LAB_ASSET_AVAILABLE',
    isApplicable: effect => !effect.value,
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf._labAssets && caseConf._labAssets.find(t => get(t, 'options.assets', []).length),
    category: featureAvailabilityService.translateLabel('Media'),
    superCategory: 'Disable',
  },
  PACING_VITAL: {
    title: 'Heart Rate',
    label: 'Change heart rate to match pacing',
    component: PacingEffect,
    options: {},
    noExpiry: true,
    type: 'VITAL',
    isApplicable: effect => effect.matchInput,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PACING'),
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  CUSTOM_STATE: {
    title: 'Custom State',
    label: 'Change a custom state',
    component: CustomStateEffect,
    noExpiry: true,
    caseSpecific: true,
    isRecommended: (caseConf) => caseConf.caseLogicDescription && caseConf.caseLogicDescription.customStates && caseConf.caseLogicDescription.customStates.length,
    category: 'Custom States',
    superCategory: 'Change',
  },
  GAME_END: {
    title: 'End Scenario',
    label: 'End the scenario',
    component: EndConditionEffect,
    noExpiry: true,
    noOutput: true,
    caseSpecific: true,
    category: 'Grading',
    superCategory: 'Grading',
  },
  GRADING: {
    title: 'Grade',
    label: 'Grade',
    component: GradingEffect,
    noExpiry: true,
    caseSpecific: true,
    category: 'Grading',
    superCategory: 'Grading',
  },
  COMPLEXION: {
    title: 'Complexion',
    label: 'Change skin condition',
    component: ComplexionEffect,
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    noExpiry: true,
    category: 'Patient Conditions',
    superCategory: 'Change',
  },
  ENABLE_CUSTOM_MENU: {
    title: 'Custom Menu',
    label: 'Enable custom menu',
    component: ToggleCustomMenuEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'ENABLE',
      },
    },
    type: 'CUSTOM_MENU',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: (caseConf) => (caseConf.customMenus || []).length > 0,
    category: 'Dialogue',
    superCategory: 'Enable',
  },
  DISABLE_CUSTOM_MENU: {
    title: 'Custom Menu',
    label: 'Disable custom menu',
    component: ToggleCustomMenuEffect,
    noExpiry: true,
    options: {
      props: {
        type: 'DISABLE',
      },
    },
    type: 'CUSTOM_MENU',
    isApplicable: effect => !effect.value,
    caseSpecific: true,
    isRecommended: (caseConf) => (caseConf.customMenus || []).length > 0,
    category: 'Dialogue',
    superCategory: 'Disable',
  },
  AI_UPDATE_PROMPT: {
    title: 'AI Prompt',
    label: 'Add to AI prompt',
    component: AiChangePromptEffect,
    noExpiry: true,
    options: {},
    type: 'AI_UPDATE_PROMPT',
    isApplicable: effect => effect.value,
    caseSpecific: true,
    isRecommended: ControlUtils.areAiEffectsRecommended,
    isDisabled: ControlUtils.isAiDisabled,
    category: 'Dialogue',
    superCategory: 'Add to',
  }
};

const EFFECTS_CONTROLS = mapValues(EFFECTS, effect => ({
  ...effect,
  control: makeVueControl(effect.component, effect.options),
}));

export default EFFECTS_CONTROLS;
